import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase';
import { doc, setDoc, runTransaction, getDoc, updateDoc, arrayUnion, serverTimestamp, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/authContext';
import TimezoneSelect from 'react-timezone-select';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailFromInvitation, setEmailFromInvitation] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Organization branding state (for invite-based signups)
  const [orgName, setOrgName] = useState('');
  const [orgLogo, setOrgLogo] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviterName, setInviterName] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser } = useAuth();
  const inviteId = new URLSearchParams(location.search).get('inviteId');
  const orgId = new URLSearchParams(location.search).get('orgId');

  const isInviteSignup = !!(inviteId && orgId);

  // Load invitation data + organization branding via Cloud Function
  // (unauthenticated users can't read Firestore directly)
  useEffect(() => {
    const fetchInviteAndOrgData = async () => {
      if (!inviteId) return;
      setInviteLoading(true);

      try {
        if (orgId) {
          // Use the Cloud Function to get invite details (works without auth)
          const functions = getFunctions();
          const getInvitationDetails = httpsCallable(functions, 'getInvitationDetails');
          const result = await getInvitationDetails({ inviteId, orgId });
          const data = result.data;

          if (data?.email) {
            setEmail(data.email);
            setEmailFromInvitation(true);
          }
          if (data?.organizationName) setOrgName(data.organizationName);
          if (data?.organizationLogo) setOrgLogo(data.organizationLogo);
          if (data?.inviterName) setInviterName(data.inviterName);
        } else {
          // Legacy fallback for old links without orgId — try Firestore (will only work if authenticated)
          try {
            const invitationsQuery = query(
              collectionGroup(db, 'invitations'),
              where('__name__', '==', inviteId)
            );
            const snapshot = await getDocs(invitationsQuery);
            if (!snapshot.empty) {
              const inviteDoc = snapshot.docs[0];
              const inviteData = inviteDoc.data();
              const pathParts = inviteDoc.ref.path.split('/');
              const foundOrgId = pathParts[1];

              if (inviteData?.email) {
                setEmail(inviteData.email);
                setEmailFromInvitation(true);
              }
              if (inviteData?.organizationName) setOrgName(inviteData.organizationName);
              if (inviteData?.inviterName) setInviterName(inviteData.inviterName);

              // Update URL to include orgId
              const newUrl = `${window.location.pathname}?orgId=${foundOrgId}&inviteId=${inviteId}`;
              window.history.replaceState({}, '', newUrl);
            }
          } catch (legacyErr) {
            console.warn('Legacy invite lookup failed (expected for unauthenticated users):', legacyErr.message);
          }
        }
      } catch (err) {
        console.error('Error fetching invitation data:', err);
        // Silently fail — the page will still work, just without pre-filled data
      } finally {
        setInviteLoading(false);
      }
    };

    fetchInviteAndOrgData();
  }, [inviteId, orgId]);

  // Redirect already-logged-in users to invitation acceptance
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const invId = urlParams.get('inviteId');
    let oId = urlParams.get('orgId');

    if (invId) {
      const auth = getAuth();
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          if (!oId) {
            try {
              const invitationsQuery = query(
                collectionGroup(db, 'invitations'),
                where('__name__', '==', invId)
              );
              const snapshot = await getDocs(invitationsQuery);
              if (!snapshot.empty) {
                const pathParts = snapshot.docs[0].ref.path.split('/');
                oId = pathParts[1];
              }
            } catch (error) {
              console.error('Error finding orgId for invitation:', error);
            }
          }

          if (oId) {
            navigate(`/accept-invitation?orgId=${oId}&inviteId=${invId}`);
          } else {
            navigate(`/accept-invitation?inviteId=${invId}`);
          }
        }
      });

      return () => unsubscribe();
    }
  }, [location.search, navigate]);

  const processInvitation = async (invId, oId, user) => {
    try {
      const inviteRef = doc(db, 'organizations', oId, 'invitations', invId);
      const inviteSnap = await getDoc(inviteRef);

      if (!inviteSnap.exists() || inviteSnap.data().status !== 'pending') {
        console.warn('Invitation not found or already processed.');
        return false;
      }

      const inviteData = inviteSnap.data();

      // Add user to org members and users (both so admin Members list shows them)
      const memberPayload = {
        role: inviteData.role || 'Member',
        status: 'active',
        joinedAt: serverTimestamp(),
        name: user.displayName || user.email || 'Member',
        email: user.email || '',
        appID: user.uid,
      };
      await setDoc(doc(db, 'organizations', oId, 'members', user.uid), memberPayload);

      // Add org to user's activeOrgs
      await updateDoc(doc(db, 'users', user.uid), {
        activeOrgs: arrayUnion(oId),
      });

      // Update user's org subcollection
      await setDoc(doc(db, 'users', user.uid, 'organizations', oId), {
        role: inviteData.role || 'Member',
        status: 'active',
        joinedAt: serverTimestamp(),
      }, { merge: true });

      // Mark invitation as accepted
      await updateDoc(inviteRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        acceptedBy: user.uid,
      });

      // License claim is handled automatically by the onInvitationAccepted
      // Cloud Function trigger when the invitation status changes to 'accepted'.
      // No client-side license operations needed.

      return true;
    } catch (error) {
      console.error('Error processing invitation post-signup:', error);
      return false;
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    const auth = getAuth();

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const appID = await generateVanityAppId();

      const userData = {
        uid: user.uid,
        email: user.email,
        name: name || 'User',
        phone: '',
        photo: '',
        role: '/roles/discipler',
        status: 'Active',
        groups: [],
        discipleIds: [],
        archivedDiscipleIds: [],
        activeOrgs: [],
        createdAt: serverTimestamp(),
        appID,
        phoneVerified: false,
        phoneVerificationId: null,
        lastVerificationAttempt: null,
        isNewUser: true,
        onboardingComplete: false,
        onboardingStep: 1,
        notificationPreferences: {
          push: { enabled: true, permission: 'default' },
          email: { enabled: true, verified: false },
          sms: { enabled: false, verified: false },
          quietHours: { enabled: true, start: '22:00', end: '08:00' },
        },
        timezone: selectedTimezone,
      };

      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, userData);

      // Generate welcome story
      const storyId = await generateStoryId();
      const userStoryRef = doc(db, 'users', user.uid, 'stories', storyId);
      await setDoc(userStoryRef, {
        text: 'Welcome to Together on Mission! This is your private story space where you can document your mission journey.',
        authorId: user.uid,
        authorName: name || 'User',
        authorEmail: user.email,
        authorPhotoURL: user.photoURL || '',
        metrics: {
          spiritualDiscussions: 0,
          heardGospel: 0,
          experiencedLove: 0,
          believersEncouraged: 0,
          newFollowers: 0,
        },
        sharing: { private: true, organizations: [] },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        status: 'active',
        id: storyId,
      });

      // Process invitation if present
      let inviteProcessed = false;
      if (inviteId) {
        let foundOrgId = orgId;
        if (!foundOrgId) {
          try {
            const invitationsQuery = query(
              collectionGroup(db, 'invitations'),
              where('__name__', '==', inviteId)
            );
            const snapshot = await getDocs(invitationsQuery);
            if (!snapshot.empty) {
              const pathParts = snapshot.docs[0].ref.path.split('/');
              foundOrgId = pathParts[1];
            }
          } catch (error) {
            console.error('Error finding orgId for invitation:', error);
          }
        }

        if (foundOrgId) {
          inviteProcessed = await processInvitation(inviteId, foundOrgId, user);
        } else {
          console.warn('Could not find organization ID for invitation:', inviteId);
        }
      }

      if (setCurrentUser) {
        setCurrentUser({ ...user, ...userData });
      }

      if (inviteProcessed && userData.onboardingComplete === false) {
        navigate('/onboarding');
      } else if (inviteProcessed) {
        navigate('/my-community');
      } else {
        navigate('/onboarding');
      }
    } catch (err) {
      console.error('Signup error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Email address is already in use. Please log in or use a different email.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateVanityAppId = async () => {
    const counterRef = doc(db, 'counters', 'userCounter');
    try {
      return await runTransaction(db, async (transaction) => {
        const counterSnap = await transaction.get(counterRef);
        const newCount = (counterSnap.exists() ? counterSnap.data().count : 0) + 1;
        transaction.set(counterRef, { count: newCount }, { merge: true });
        return `UID${String(newCount).padStart(3, '0')}`;
      });
    } catch (error) {
      throw new Error('Unable to generate user ID');
    }
  };

  const generateStoryId = async () => {
    const counterRef = doc(db, 'counters', 'storyCounter');
    try {
      return await runTransaction(db, async (transaction) => {
        const counterSnap = await transaction.get(counterRef);
        const newCount = (counterSnap.exists() ? counterSnap.data().count : 0) + 1;
        transaction.set(counterRef, { count: newCount }, { merge: true });
        return `storyID_${String(newCount).padStart(5, '0')}`;
      });
    } catch (error) {
      throw new Error('Unable to generate story ID');
    }
  };

  return (
    <div className="min-h-screen relative bg-gray-900">
      {/* Background Image - same as Login */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://i0.wp.com/togetheronmission.org/wp-content/uploads/2016/11/cropped-group_like.jpg?fit=1286%2C857&ssl=1"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gray-900/35"></div>
      </div>

      {/* Signup Card - glass style matching Login */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
        <div className="relative w-full max-w-md">
          {/* Blur layer */}
          <div
            className="absolute inset-0 rounded-2xl overflow-hidden backdrop-blur-xl"
            aria-hidden="true"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          />
          {/* Content layer */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/25 ring-1 ring-inset ring-white/20">
            {/* Card Header */}
            <div
              className="px-8 pt-8 pb-6 text-center"
              style={{ background: 'rgba(17,24,39,0.78)' }}
            >
              {isInviteSignup && orgLogo ? (
                <img
                  src={orgLogo}
                  alt={orgName || 'Organization'}
                  className="h-14 w-auto mx-auto mb-4 rounded-lg object-contain"
                />
              ) : (
                <img
                  src="/images/TOM-logo-512.png"
                  alt="Together On Mission"
                  className="h-14 w-auto mx-auto mb-4"
                />
              )}
              <h2 className="text-2xl font-bold text-white mb-1">
                {isInviteSignup ? 'Join your Discipleship Team' : 'Join the Community'}
              </h2>
              {isInviteSignup && inviterName ? (
                <p className="text-gray-300 text-sm">
                  {inviterName} invited you to {orgName || 'the team'}
                </p>
              ) : (
                <p className="text-gray-300 text-sm">
                  Create your account to get started
                </p>
              )}
            </div>

            {/* Card Body */}
            <div className="px-8 py-8" style={{ background: 'rgba(0,0,0,0.2)' }}>
              {error && (
                <div className="mb-5 p-3 rounded-lg bg-red-50/90 border border-red-200 text-red-600 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSignup} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-200 mb-1.5">Name</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="block w-full px-4 py-3 border border-gray-400/60 rounded-xl shadow-sm bg-white/95 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-1.5">
                    Email
                    {emailFromInvitation && (
                      <span className="ml-2 text-xs text-green-400 font-normal">(from invitation)</span>
                    )}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    readOnly={emailFromInvitation}
                    className={`block w-full px-4 py-3 border rounded-xl shadow-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition ${
                      emailFromInvitation ? 'border-gray-400/60 bg-gray-100/90 cursor-not-allowed' : 'border-gray-400/60 bg-white/95'
                    }`}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-200 mb-1.5">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-4 py-3 border border-gray-400/60 rounded-xl shadow-sm bg-white/95 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-200 mb-1.5">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full px-4 py-3 border border-gray-400/60 rounded-xl shadow-sm bg-white/95 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-1.5">Your Timezone</label>
                  <div className="mt-1">
                    <TimezoneSelect
                      value={selectedTimezone}
                      onChange={setSelectedTimezone}
                      className="block w-full px-3 py-2 border border-gray-400/60 rounded-xl shadow-sm bg-white/95 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary sm:text-sm"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    This helps us send reminders at the right time for you
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-base font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating Account...' : isInviteSignup ? 'Join Team' : 'Sign Up'}
                </button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-400/60"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 text-gray-300" style={{ background: 'rgba(17,24,39,0.95)' }}>Already have an account?</span>
                </div>
              </div>

              <Link
                to={inviteId && orgId ? `/login?orgId=${orgId}&inviteId=${inviteId}` : '/login'}
                className="w-full flex justify-center py-3 px-4 border-2 border-white rounded-xl text-base font-semibold text-white hover:bg-white hover:text-gray-900 transition-colors"
              >
                Sign in
              </Link>
            </div>
          </div>

          <p className="text-center text-white/50 text-xs mt-6">
            Go make disciples. &mdash; Matthew 28:19
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
